import { EmblaCarouselType, EmblaOptionsType } from "embla-carousel";
import useEmblaCarousel from "embla-carousel-react";
import React, { useCallback, useEffect, useState } from "react";
import styled from "styled-components";

const GalleryWrap = styled.div<{ dotsBottom?: boolean }>`
  .embla {
    position: relative;
    --slide-height: 19rem;
    --slide-size: 100%;
  }
  .embla__viewport {
    overflow: hidden;
  }
  .embla__container {
    display: flex;
    touch-action: pan-y pinch-zoom;
  }
  .embla__slide {
    transform: translate3d(0, 0, 0);
    flex: 0 0 var(--slide-size);
    aspect-ratio: 1 / 1;
    min-width: 0;
  }
  .dots {
    pointer-events: none;
    position: absolute;
    bottom: ${({ dotsBottom }) => (dotsBottom ? "10px" : "40px")};
    width: 100%;
    display: flex;
    gap: 8px;
    z-index: 4;
    flex-wrap: wrap;
    justify-content: center;
    align-items: center;
  }
  .dot {
    background-color: white;
    opacity: 0.5;
    height: 0.5rem;
    width: 0.5rem;
    border-radius: 50%;

    &.selected {
      opacity: 1;
      background-color: white;
      box-shadow: 0 0 0 2px rgba(0, 0, 0, 0.16);
    }
  }
`;

interface GalleryProps {
  slides: React.ReactNode[];
  options?: EmblaOptionsType;
  dotsBottom?: boolean;
}

const Gallery: React.FunctionComponent<GalleryProps> = (props) => {
  const { slides, dotsBottom } = props;
  const [emblaRef, emblaApi] = useEmblaCarousel({ loop: true });

  const { selectedIndex, scrollSnaps } = useDotButton(emblaApi);

  return (
    <GalleryWrap dotsBottom={dotsBottom}>
      <section className="embla">
        <div className="embla__viewport" ref={emblaRef}>
          <div className="embla__container">
            {slides.map((item, index) => (
              <div className="embla__slide" key={index}>
                {item}
              </div>
            ))}
          </div>
        </div>

        <div className="dots">
          {scrollSnaps.map((_, index) => (
            <DotButton
              key={index}
              className={"dot".concat(
                index === selectedIndex ? " selected" : ""
              )}
            />
          ))}
        </div>
      </section>
    </GalleryWrap>
  );
};

export default Gallery;

type UseDotButtonType = {
  selectedIndex: number;
  scrollSnaps: number[];
  onDotButtonClick: (index: number) => void;
};

export const useDotButton = (
  emblaApi: EmblaCarouselType | undefined
): UseDotButtonType => {
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [scrollSnaps, setScrollSnaps] = useState<number[]>([]);

  const onDotButtonClick = useCallback(
    (index: number) => {
      if (!emblaApi) return;
      emblaApi.scrollTo(index);
    },
    [emblaApi]
  );

  const onInit = useCallback((emblaApi: EmblaCarouselType) => {
    setScrollSnaps(emblaApi.scrollSnapList());
  }, []);

  const onSelect = useCallback((emblaApi: EmblaCarouselType) => {
    setSelectedIndex(emblaApi.selectedScrollSnap());
  }, []);

  useEffect(() => {
    if (!emblaApi) return;

    onInit(emblaApi);
    onSelect(emblaApi);
    emblaApi.on("reInit", onInit).on("reInit", onSelect).on("select", onSelect);
  }, [emblaApi, onInit, onSelect]);

  return {
    selectedIndex,
    scrollSnaps,
    onDotButtonClick
  };
};

export const DotButton: React.FC<any> = (props) => {
  const { children, onClick, ...restProps } = props;

  return <span {...restProps}>{children}</span>;
};
